import { create } from "zustand";

interface ExperienceGain {
  id: string;
  amount: number;
  timestamp: number;
}

interface ExperienceGainState {
  // Active experience gains
  activeGains: ExperienceGain[];
  // Total experience (should sync with user data)
  currentExperience: number;
  currentLevel: number;

  // Actions
  addExperienceGain: (amount: number) => void;
  removeExperienceGain: (id: string) => void;
  updateCurrentExperience: (experience: number, level: number) => void;
  clearAllGains: () => void;
}

export const useExperienceGainStore = create<ExperienceGainState>(
  (set, _get) => ({
    activeGains: [],
    currentExperience: 0,
    currentLevel: 1,

    addExperienceGain: (amount: number) => {
      const id = `exp-${Date.now()}-${Math.random()}`;
      const gain: ExperienceGain = {
        id,
        amount,
        timestamp: Date.now()
      };

      set((state) => ({
        activeGains: [...state.activeGains, gain],
        currentExperience: state.currentExperience + amount
      }));

      // Auto-remove after 3 seconds (safety cleanup)
      setTimeout(() => {
        set((state) => ({
          activeGains: state.activeGains.filter((g) => g.id !== id)
        }));
      }, 3000);
    },

    removeExperienceGain: (id: string) => {
      set((state) => ({
        activeGains: state.activeGains.filter((g) => g.id !== id)
      }));
    },

    updateCurrentExperience: (experience: number, level: number) => {
      set({
        currentExperience: experience,
        currentLevel: level
      });
    },

    clearAllGains: () => {
      set({ activeGains: [] });
    }
  })
);

// Experience gain trigger functions
export const triggerExperienceGain = (amount: number, _reason?: string) => {
  useExperienceGainStore.getState().addExperienceGain(amount);
};

// Common experience gain amounts for different actions
export const EXPERIENCE_GAINS = {
  LIKE_MESSAGE: 100,
  SEND_MESSAGE: 100,
  CREATE_POST: 100,
  COMPLETE_TASK: 100,
  DAILY_LOGIN: 100,
  FIRST_LOGIN: 100,
  COMPLETE_PROFILE: 100,
  RECEIVE_APPRECIATION: 100,
  GIVE_APPRECIATION: 100,
  CREATE_REQUEST: 100,
  CREATE_APPRECIATION: 100,
  CREATE_CAREER: 100,
  CREATE_ITEM_PATH: 100,
  CREATE_OFFER: 100,
  CREATE_APPLICANT: 100,
  MOVE_APPLICANT: 100
} as const;
